import {AxiosError} from 'axios'
import {DefaultApiResponse} from 'models/ApiResponse'
import {IFormField} from 'models/Common'
import {
 CreateCropPayload,
 CreateCropResponse,
 CropDetailResponse,
 CropQualityParameterMappingResponse,
 ICropDetail,
 ICropMaster,
 IQualityParameterDefinition,
 UpdateCropPayload,
 UpdateCropResponse,
} from 'models/CropMaster'
import {toast} from 'react-toastify'
import {get, patch, post} from 'utils/axios'
import {handleRejectedErrorMsg} from 'utils/helpers/handleError'

const extractCropMasterList = (
 res: DefaultApiResponse | null | undefined
): ICropMaster[] => {
 const candidates = [
  res?.data,
  (res?.data as Record<string, unknown> | undefined)
   ?.results,
  (res?.data as Record<string, unknown> | undefined)?.data,
  res?.results,
 ]
 const match = candidates.find((candidate) =>
  Array.isArray(candidate)
 )
 return (match as ICropMaster[]) ?? []
}

const extractCropMasterCount = (
 res: DefaultApiResponse | null | undefined,
 fallbackLength: number
): number => {
 const responseRecord = res as unknown as
  | Record<string, unknown>
  | undefined
 const dataRecord = res?.data as
  | Record<string, unknown>
  | undefined
 const candidates = [
  dataRecord?.total_count,
  responseRecord?.total_count,
 ]
 const match = candidates.find(
  (candidate) => typeof candidate === 'number'
 )
 return typeof match === 'number' ? match : fallbackLength
}

const extractCropMasterActiveCount = (
 res: DefaultApiResponse | null | undefined,
 fallbackCount: number
): number => {
 const responseRecord = res as unknown as
  | Record<string, unknown>
  | undefined
 const dataRecord = res?.data as
  | Record<string, unknown>
  | undefined
 const candidates = [
  dataRecord?.active_count,
  responseRecord?.active_count,
 ]
 const match = candidates.find(
  (candidate) => typeof candidate === 'number'
 )
 return typeof match === 'number' ? match : fallbackCount
}

const fetchCropMasterList = async (
 page: number,
 pageSize: number,
 query?: IFormField,
 signal?: AbortSignal
): Promise<{
 data: ICropMaster[]
 total: number
 activeCount: number
}> => {
 try {
  const params: IFormField = {
   page,
   page_size: pageSize,
   ...query,
  }
  const res = await get<DefaultApiResponse>(
   '/crop/crop-master',
   params,
   undefined,
   undefined,
   signal
  )
  if (res?.success) {
   const list = extractCropMasterList(res)
   const fallbackActiveCount = list.filter(
    (crop) => crop.is_active
   ).length
   return {
    data: list,
    total: extractCropMasterCount(res, list.length),
    activeCount: extractCropMasterActiveCount(
     res,
     fallbackActiveCount
    ),
   }
  }
  toast.error('Something went wrong')
  return {data: [], total: 0, activeCount: 0}
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)

  return {data: [], total: 0, activeCount: 0}
 }
}

const createCrop = async (
 payload: CreateCropPayload
): Promise<ICropDetail | null> => {
 try {
  const res = await post<CreateCropResponse>(
   '/crop/crop-master',
   payload
  )
  if (res?.success) {
   toast.success(
    res?.message || 'Crop created successfully'
   )
   return res.data
  }
  toast.error(res?.message || 'Something went wrong')
  return null
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const fetchCropDetails = async (
 cropId: number
): Promise<ICropDetail | null> => {
 try {
  const res = await get<CropDetailResponse>(
   `/crop/crop-master/${cropId}`
  )
  if (res?.success) {
   return res.data
  }
  toast.error(res?.message || 'Something went wrong')
  return null
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const updateCrop = async (
 cropId: number,
 payload: UpdateCropPayload
): Promise<ICropDetail | null> => {
 try {
  const res = await patch<UpdateCropResponse>(
   `/crop/crop-master/${cropId}`,
   payload
  )
  if (res?.success) {
   toast.success(
    res?.message || 'Crop updated successfully'
   )
   return res.data
  }
  toast.error(res?.message || 'Something went wrong')
  return null
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const deleteCropQualityParameter = async (
 mappingId: number
): Promise<boolean> => {
 try {
  const res =
   await patch<CropQualityParameterMappingResponse>(
    `/crop/crop-quality-parameter/${mappingId}`,
    {is_active: false}
   )
  if (res?.success) {
   toast.success(
    res?.message || 'Quality parameter removed successfully'
   )
   return true
  }
  toast.error(res?.message || 'Something went wrong')
  return false
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return false
 }
}

const extractQualityParameterList = (
 res: DefaultApiResponse | null | undefined
): IQualityParameterDefinition[] => {
 const candidates = [
  res?.data,
  (res?.data as Record<string, unknown> | undefined)
   ?.results,
  (res?.data as Record<string, unknown> | undefined)?.data,
  res?.results,
 ]
 const match = candidates.find((candidate) =>
  Array.isArray(candidate)
 )
 return (match as IQualityParameterDefinition[]) ?? []
}

const fetchQualityParameters = async (): Promise<
 IQualityParameterDefinition[] | null
> => {
 try {
  const res = await get<DefaultApiResponse>(
   '/crop/quality-parameters'
  )
  if (res?.success) {
   return extractQualityParameterList(res)
  }
  toast.error(res?.message || 'Something went wrong')
  return null
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

export {
 fetchCropMasterList,
 createCrop,
 fetchCropDetails,
 updateCrop,
 fetchQualityParameters,
 deleteCropQualityParameter,
}
